import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Mail, ArrowLeft, Building2 } from 'lucide-react';
import { ASSETS } from '../../mock/data';
import { useApp } from '../../context/AppContext';

const FranchiseLogin = () => {
  const nav = useNavigate();
  const { login } = useApp();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(''); setLoading(true);
    try {
      const u = await login({ email, password });
      if (u.role !== 'franchise_admin') { setErr('This account is not a franchise owner. Please use the correct workspace login.'); return; }
      nav('/franchise');
    } catch (ex) {
      setErr(ex.response?.data?.detail || 'Invalid credentials');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-neutral-100 flex items-center justify-center px-5" data-testid="franchise-login-page">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="px-6 pt-6">
          <button onClick={() => nav('/')} data-testid="franchise-login-back" className="text-sm text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back to site
          </button>
        </div>
        <div className="px-6 pt-4 pb-8">
          <div className="bg-[#7B1FA2] rounded-xl p-4 inline-flex items-center gap-3">
            <div className="bg-white rounded p-1">
              <img src={ASSETS.logo} className="h-10" alt="Clean24" />
            </div>
            <div>
              <p className="text-white font-bold text-sm">Clean24 Franchise Owner</p>
              <p className="text-purple-100 text-xs">District-level P&amp;L, staff & payouts</p>
            </div>
          </div>

          <h1 className="mt-5 text-2xl font-extrabold text-neutral-900">Franchise Owner login</h1>
          <p className="text-sm text-neutral-500 mt-1">Access your district ERP: bookings, wallet, payroll, GST & banking.</p>

          {err && <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg" data-testid="franchise-login-error">{err}</div>}

          <form onSubmit={submit} className="mt-5 space-y-3">
            <div className="relative">
              <Mail className="w-4 h-4 text-neutral-400 absolute left-4 top-1/2 -translate-y-1/2" />
              <input type="email" required placeholder="admin.kollam@clean24.com" value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="franchise-login-email"
                className="w-full pl-11 pr-4 py-3 rounded-xl border border-neutral-200 bg-white focus:outline-none focus:border-[#7B1FA2] focus:ring-2 focus:ring-[#7B1FA2]/20 text-sm" />
            </div>
            <div className="relative">
              <Lock className="w-4 h-4 text-neutral-400 absolute left-4 top-1/2 -translate-y-1/2" />
              <input type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="franchise-login-password"
                className="w-full pl-11 pr-4 py-3 rounded-xl border border-neutral-200 bg-white focus:outline-none focus:border-[#7B1FA2] focus:ring-2 focus:ring-[#7B1FA2]/20 text-sm" />
            </div>

            <button type="submit" disabled={loading} data-testid="franchise-login-submit"
              className="w-full py-3 rounded-xl bg-[#7B1FA2] hover:bg-[#6A1B9A] disabled:opacity-70 text-white font-semibold transition-colors">
              {loading ? 'Signing in...' : 'Sign in as Franchise Owner'}
            </button>
          </form>

          <div className="mt-4 p-3 bg-purple-50 border border-purple-100 rounded-lg text-xs text-purple-900">
            <p className="font-semibold flex items-center gap-1"><Building2 className="w-3.5 h-3.5" /> Need an account?</p>
            <p className="mt-1">Ask your Super Admin to create a franchise for your district. They will share your login credentials.</p>
          </div>

          <div className="mt-3 text-center text-xs text-neutral-500">
            Not a franchise owner? <button onClick={() => nav('/login')} className="text-[#7B1FA2] font-semibold hover:underline" data-testid="franchise-login-customer-link">Customer login</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FranchiseLogin;
